import {
  ArrowRight,
  BellRing,
  FileQuestion,
  GitCompareArrows,
  Globe,
  ListOrdered,
  Loader2,
  Scale,
  Send,
  Server,
  ShieldCheck,
  Siren,
  Target,
  TriangleAlert,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { api, type JobQueueSummary, type OpsOverview, type OpsRegistry, type Slo } from '../../api';
import { Badge, TONE_CLASS, TONE_ICON, type Tone } from '../../components/Badge';
import { Callout, Section, StatCard } from '../../components/blocks';
import { DataError, isNotConfigured } from '../../components/DataError';
import { Meter } from '../../components/Meter';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState } from '../../components/States';
import { formatCount, formatPercent, formatRelative } from '../../lib/format';
import { attentionItems, budgetRemaining, budgetTone, evaluateOverview, STATUS_TONE, sumValues, type AttentionItem, type MetricStatus, type MetricTile, type OpsT, type TileGroup } from './metrics';
import { Footnote, GroupHeading, LinkButton, RefreshButton, useEnumLabel, useLive, type Live } from './shared';

const REFRESH_MS = 30_000;
const AUTO_KEY = 'cc.ops.autoRefresh';
const ATTENTION_PREVIEW = 6;

const TILE_ICON: Record<string, LucideIcon> = {
  unknown: FileQuestion,
  recon: GitCompareArrows,
  workflows: Workflow,
  ledger: Scale,
  compliance: ShieldCheck,
  incidents: Siren,
  alerts: BellRing,
  queues: ListOrdered,
  outbox: Send,
  services: Server,
  slo: Target,
};

const FILL: Record<Tone, string> = {
  neutral: 'bg-muted-foreground/60',
  info: 'bg-info',
  ok: 'bg-success',
  pending: 'bg-info',
  warn: 'bg-warning',
  serious: 'bg-serious',
  crit: 'bg-critical',
};

const TX_TONE: Record<string, Tone> = { PENDING: 'pending', SENT: 'pending', UNKNOWN: 'serious', RECONCILING: 'serious', MANUAL_REVIEW: 'crit', RETRY_PENDING: 'warn' };
const WF_TONE: Record<string, Tone> = { running: 'pending', waiting: 'info', retrying: 'warn', manual_review: 'crit', compensating: 'warn', failed: 'crit' };

function readAutoRefresh(): boolean {
  try {
    return window.localStorage.getItem(AUTO_KEY) !== 'off';
  } catch {
    return true;
  }
}

function writeAutoRefresh(on: boolean): void {
  try {
    window.localStorage.setItem(AUTO_KEY, on ? 'on' : 'off');
  } catch {
    // Storage unavailable: the choice lasts for this page only.
  }
}

const utcTime = (ms: number): string => `${new Date(ms).toISOString().slice(11, 19)} UTC`;

/** Tile for a list endpoint that may be unavailable (not configured, failed, still loading). */
function unavailableTile<T>(live: Live<T>, base: Omit<MetricTile, 'value' | 'status' | 'caption'>, t: OpsT): MetricTile | null {
  if (live.data !== undefined) return null;
  const caption = live.error ? (isNotConfigured(live.error) ? t('shared.notConfigured') : t('shared.loadFailed')) : '…';
  return { ...base, value: '—', status: 'unknown', caption };
}

// ---------------------------------------------------------------------------

function HealthChips({ items, unknown }: { items: AttentionItem[]; unknown: number }) {
  const { t } = useTranslation('ops');
  const crit = items.filter((i) => i.tone === 'crit' || i.tone === 'serious').length;
  const warn = items.filter((i) => i.tone === 'warn').length;
  const chips: { tone: Tone; text: string }[] = [];
  if (crit) chips.push({ tone: 'crit', text: t('overview.summary.crit', { count: crit }) });
  if (warn) chips.push({ tone: 'warn', text: t('overview.summary.warn', { count: warn }) });
  if (!crit && !warn) chips.push({ tone: 'ok', text: t('overview.summary.healthy') });
  if (unknown) chips.push({ tone: 'neutral', text: t('overview.summary.unknown', { count: unknown }) });
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label={t('overview.summary.label')}>
      {chips.map((c) => {
        const Icon = TONE_ICON[c.tone];
        const chip = (
          <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold', TONE_CLASS[c.tone])}>
            {Icon ? <Icon className="size-3.5" aria-hidden="true" /> : null}
            {c.text}
          </span>
        );
        return c.tone === 'crit' || c.tone === 'warn' ? (
          <a key={c.tone} href="#attention" className="rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
            {chip}
          </a>
        ) : (
          <span key={c.tone}>{chip}</span>
        );
      })}
    </div>
  );
}

function RefreshControls({ auto, onAuto, at, busy, stale, onRefresh }: { auto: boolean; onAuto: (on: boolean) => void; at: number | null; busy: boolean; stale: boolean; onRefresh: () => void }) {
  const { t } = useTranslation('ops');
  const id = useId();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status" aria-live="polite">
        {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : null}
        {stale ? <TriangleAlert className="size-3.5 text-warning" aria-hidden="true" /> : null}
        <span title={stale ? t('overview.staleHint') : undefined}>{at ? t('overview.updated', { time: utcTime(at) }) : t('overview.updating')}</span>
      </span>
      <div className="flex items-center gap-2" title={t('overview.autoRefreshHint')}>
        <Switch id={`${id}-auto`} checked={auto} onCheckedChange={onAuto} />
        <Label htmlFor={`${id}-auto`} className="text-sm font-normal">
          {t('overview.autoRefresh')}
        </Label>
      </div>
      <RefreshButton onClick={onRefresh} busy={busy} />
    </div>
  );
}

function AttentionList({ items }: { items: AttentionItem[] }) {
  const { t } = useTranslation('ops');
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, ATTENTION_PREVIEW);
  return (
    <Section
      title={
        <span className="flex items-center gap-2" id="attention">
          <TriangleAlert className="size-4 text-serious" aria-hidden="true" />
          {t('overview.attention.title')}
          <Badge tone={items.some((i) => i.tone === 'crit') ? 'crit' : 'warn'} icon={false}>
            {items.length}
          </Badge>
        </span>
      }
      description={t('overview.attention.description')}
      flush
      className="scroll-mt-20"
    >
      <ul className="divide-y border-t">
        {shown.map((item) => {
          const Icon = TONE_ICON[item.tone] ?? TriangleAlert;
          return (
            <li key={item.id}>
              <Link to={item.to} className="group flex items-start gap-3 px-5 py-3 text-foreground no-underline transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none">
                <span className={cn('mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border', TONE_CLASS[item.tone])}>
                  <Icon className="size-3.5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 space-y-0.5">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium break-words">{item.title}</span>
                    {item.code ? (
                      <Badge tone={item.tone} icon={false}>
                        {item.code}
                      </Badge>
                    ) : null}
                    <span className="rounded border px-1.5 text-[0.7rem] font-medium tracking-wide text-muted-foreground uppercase">{t(`overview.attention.source.${item.source}`)}</span>
                  </span>
                  <span className="block text-sm text-muted-foreground">{item.detail}</span>
                </span>
                <span className="hidden shrink-0 items-center gap-1 self-center text-sm font-medium text-primary group-hover:underline sm:inline-flex">
                  {t('overview.attention.act')} <ArrowRight className="size-3.5" aria-hidden="true" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {items.length > ATTENTION_PREVIEW ? (
        <div className="border-t px-5 py-2">
          <button type="button" className="text-sm font-medium text-primary hover:underline" onClick={() => setAll((v) => !v)} aria-expanded={all}>
            {all ? t('shared.showLess') : t('shared.showAll', { count: items.length })}
          </button>
        </div>
      ) : null}
    </Section>
  );
}

function Tile({ tile }: { tile: MetricTile }) {
  const { t } = useTranslation('ops');
  const tone = STATUS_TONE[tile.status];
  return (
    <StatCard
      label={tile.label}
      value={tile.value}
      tone={tone}
      status={t(`status.${tile.status}`)}
      icon={TILE_ICON[tile.id]}
      hint={tile.caption}
      to={tile.to}
      linkLabel={tile.toLabel}
    >
      {tile.alert && (tile.status === 'warn' || tile.status === 'crit') ? <p className={cn('text-xs font-medium', tile.status === 'crit' ? 'text-critical' : 'text-warning')}>{tile.alert}</p> : null}
    </StatCard>
  );
}

function TileSkeleton() {
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-3 w-full" />
    </div>
  );
}

function Group({ id, icon, tiles, loading, className, gridClassName, children }: { id: TileGroup; icon: LucideIcon; tiles: MetricTile[]; loading: boolean; className?: string; gridClassName?: string; children?: ReactNode }) {
  const { t } = useTranslation('ops');
  const mine = tiles.filter((x) => x.group === id);
  return (
    <section className={className} aria-labelledby={`group-${id}`}>
      <div id={`group-${id}`}>
        <GroupHeading icon={icon} title={t(`overview.group.${id}`)} hint={t(`overview.group.${id}Hint`)} />
      </div>
      <div className={cn('grid grid-cols-1 gap-4', gridClassName)}>
        {loading && mine.length === 0 ? <TileSkeleton /> : mine.map((tile) => <Tile key={tile.id} tile={tile} />)}
        {children}
      </div>
    </section>
  );
}

/** Horizontal stacked bars: ready / running / retrying / dead per job queue. */
function QueueDepth({ queues }: { queues: JobQueueSummary[] }) {
  const { t } = useTranslation('ops');
  const parts = [
    { key: 'ready', cls: 'bg-chart-1' },
    { key: 'running', cls: 'bg-chart-2' },
    { key: 'retrying', cls: 'bg-warning' },
    { key: 'dead', cls: 'bg-critical' },
  ] as const;
  const total = (q: JobQueueSummary) => q.ready + q.running + q.retrying + q.dead;
  const max = Math.max(1, ...queues.map(total));
  if (queues.length === 0) return <EmptyState title={t('overview.queueDepth.empty')} />;
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-hidden="true">
        {parts.map((p) => (
          <li key={p.key} className="inline-flex items-center gap-1.5">
            <span className={cn('size-2.5 rounded-sm', p.cls)} />
            {t(`overview.queueDepth.${p.key}`)}
          </li>
        ))}
      </ul>
      <ul className="flex flex-col gap-3">
        {[...queues]
          .sort((a, b) => total(b) - total(a))
          .map((q) => (
            <li
              key={q.queue}
              className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 sm:grid-cols-[minmax(0,12rem)_1fr_auto]"
              aria-label={t('overview.queueDepth.rowLabel', { queue: q.queue, ready: q.ready, running: q.running, retrying: q.retrying, dead: q.dead })}
            >
              <code className="truncate text-xs" title={q.queue}>
                {q.queue}
              </code>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                {parts.map((p) => (q[p.key] > 0 ? <div key={p.key} className={cn('h-full', p.cls)} style={{ width: `${(q[p.key] / max) * 100}%` }} title={`${t(`overview.queueDepth.${p.key}`)}: ${q[p.key]}`} /> : null))}
              </div>
              <span className="flex items-center justify-end gap-1.5 text-right text-xs whitespace-nowrap">
                <span className="tabular font-semibold">{formatCount(total(q))}</span>
                {q.dead ? (
                  <Badge tone="crit" icon={false}>
                    {q.dead}
                  </Badge>
                ) : null}
                <span className="hidden text-muted-foreground md:inline">{t('overview.queueDepth.scheduled', { count: q.scheduled })}</span>
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}

function StateBars({ title, counts, tones, group }: { title: string; counts: Record<string, number>; tones: Record<string, Tone>; group: 'commandState' | 'workflowState' }) {
  const label = useEnumLabel();
  const rows = Object.entries(counts).filter(([, n]) => n > 0).sort(([, a], [, b]) => b - a);
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title} <span className="tabular font-normal">· {formatCount(sumValues(counts))}</span>
      </h3>
      <ul className="flex flex-col gap-2">
        {rows.map(([state, n]) => {
          const tone = tones[state] ?? tones[state.toLowerCase()] ?? 'neutral';
          return (
            <li key={state} className="grid grid-cols-[minmax(0,10.5rem)_1fr_auto] items-center gap-3 text-sm">
              <span className="truncate" title={state}>
                {label(group, state)}
              </span>
              <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                <div className={cn('h-full rounded-full', FILL[tone])} style={{ width: `${(n / max) * 100}%` }} />
              </div>
              <span className="tabular text-right font-semibold">{formatCount(n)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function InFlight({ o }: { o: OpsOverview }) {
  const { t } = useTranslation('ops');
  const tx = sumValues(o.registry_transactions_open);
  const wf = sumValues(o.workflows_in_flight);
  if (!tx && !wf) return <EmptyState title={t('overview.inFlight.empty')} />;
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <StateBars title={t('overview.inFlight.transactions')} counts={o.registry_transactions_open} tones={TX_TONE} group="commandState" />
      <StateBars title={t('overview.inFlight.workflows')} counts={o.workflows_in_flight} tones={WF_TONE} group="workflowState" />
    </div>
  );
}

function Budgets({ slos }: { slos: Slo[] }) {
  const { t } = useTranslation('ops');
  const rows = slos
    .map((s) => ({ s, remaining: budgetRemaining(s) }))
    .sort((a, b) => (a.remaining ?? 2) - (b.remaining ?? 2))
    .slice(0, 6);
  if (rows.length === 0) return <EmptyState title={t('overview.budgets.empty')} />;
  return (
    <ul className="flex flex-col gap-3">
      {rows.map(({ s, remaining }, i) => {
        const name = s.name ?? s.id ?? 'SLO';
        const tone = budgetTone(remaining);
        return (
          <li key={s.id ?? i} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate" title={name}>
                {name} <code className="text-xs text-muted-foreground">{s.service}</code>
              </span>
              <span className={cn('tabular shrink-0 font-semibold', tone === 'crit' && 'text-critical', tone === 'warn' && 'text-warning')}>
                {remaining === null ? '—' : remaining <= 0 ? t('slo.exhausted') : formatPercent(remaining, 0)}
              </span>
            </div>
            <Meter value={remaining === null ? null : Math.max(0, remaining)} tone={tone} label={t('slo.meterLabel', { name })} />
          </li>
        );
      })}
    </ul>
  );
}

function Registries({ items }: { items: OpsRegistry[] }) {
  const { t } = useTranslation('ops');
  if (items.length === 0) return <EmptyState title={t('overview.registries.empty')} />;
  return (
    <ul className="divide-y border-t">
      {items.map((r) => (
        <li key={r.code}>
          <Link to={`/ops/registries?registry=${encodeURIComponent(r.code)}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-foreground no-underline hover:bg-muted/50">
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{r.code}</span> <span className="text-sm text-muted-foreground">{r.name}</span>
              <span className="block text-xs text-muted-foreground">
                {r.last_success_at ? t('overview.registries.lastSuccess', { ago: formatRelative(r.last_success_at) }) : t('overview.registries.never')}
              </span>
            </span>
            <Badge tone="neutral" icon={false}>
              {t('overview.registries.open', { count: r.open_transactions })}
            </Badge>
            {r.unknown_transactions ? <Badge tone="serious">{t('overview.registries.unknown', { count: r.unknown_transactions })}</Badge> : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SectionError({ live }: { live: Live<unknown> }) {
  return live.data === undefined && live.error ? <DataError error={live.error} onRetry={live.reload} /> : null;
}

// ---------------------------------------------------------------------------

/** `/ops/overview`: the Control Center home: health at a glance, what needs attention, where to act. */
export function OpsOverviewPage() {
  const { t } = useTranslation('ops');
  const label = useEnumLabel();
  useDocumentTitle(t('overview.title'));
  const [auto, setAuto] = useState(readAutoRefresh);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!auto) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setTick((n) => n + 1);
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [auto]);

  const overview = useLive((signal) => api.getOpsOverview({ signal }), tick);
  const services = useLive((signal) => api.listOpsServices({ signal }), tick);
  const registries = useLive((signal) => api.listOpsRegistries({ signal }), tick);
  const alerts = useLive((signal) => api.listAlerts({ signal }), tick);
  const incidents = useLive((signal) => api.listIncidents({ signal }), tick);
  const slos = useLive((signal) => api.listSlos({ signal }), tick);
  const all = [overview, services, registries, alerts, incidents, slos] as Live<unknown>[];
  const busy = all.some((l) => l.loading);

  const o = overview.data;
  const tiles: MetricTile[] = o ? evaluateOverview(o, t) : [];

  // Alerts (response)
  const alertBase = { id: 'alerts', group: 'response' as const, label: t('overview.tile.alerts.label'), to: '/ops/alerts', toLabel: t('overview.tile.alerts.link') };
  const activeAlerts = alerts.data?.items.filter((a) => a.status.state === 'active') ?? [];
  const silenced = (alerts.data?.items.length ?? 0) - activeAlerts.length;
  const alertStatus: MetricStatus = activeAlerts.some((a) => a.labels.severity === 'P0')
    ? 'crit'
    : activeAlerts.some((a) => a.labels.severity === 'P1' || a.labels.severity === 'P2')
      ? 'warn'
      : 'ok';
  tiles.push(
    unavailableTile(alerts, alertBase, t) ?? {
      ...alertBase,
      value: formatCount(activeAlerts.length),
      status: alertStatus,
      caption: t('overview.tile.alerts.caption', { silenced }),
    },
  );

  // Services (platform)
  const svcBase = { id: 'services', group: 'platform' as const, label: t('overview.tile.services.label'), to: '/ops/services', toLabel: t('overview.tile.services.link') };
  const svc = services.data?.items ?? [];
  const svcNames = new Set(svc.map((s) => s.service ?? '—'));
  const deadServices = [...svcNames].filter((n) => svc.filter((s) => (s.service ?? '—') === n).every((s) => !s.up)).length;
  const up = svc.filter((s) => s.up).length;
  tiles.push(
    unavailableTile(services, svcBase, t) ?? {
      ...svcBase,
      value: `${formatCount(up)}/${formatCount(svc.length)}`,
      status: svc.length === 0 ? 'unknown' : deadServices ? 'crit' : up < svc.length ? 'warn' : 'ok',
      caption: t('overview.tile.services.caption', { services: svcNames.size }),
    },
  );

  // SLOs (platform)
  const sloBase = { id: 'slo', group: 'platform' as const, label: t('overview.tile.slo.label'), to: '/ops/slo', toLabel: t('overview.tile.slo.link') };
  const sloItems = slos.data?.items ?? [];
  const remaining = sloItems.map(budgetRemaining).filter((r): r is number => r !== null);
  const met = remaining.filter((r) => r > 0).length;
  tiles.push(
    unavailableTile(slos, sloBase, t) ?? {
      ...sloBase,
      value: `${met}/${remaining.length}`,
      status: remaining.length === 0 ? 'unknown' : remaining.some((r) => r <= 0) ? 'crit' : remaining.some((r) => r <= 0.25) ? 'warn' : 'ok',
      caption: t('overview.tile.slo.caption', { low: remaining.filter((r) => r <= 0.25).length }),
    },
  );

  const items = o
    ? attentionItems({
        tiles,
        alerts: alerts.data?.items,
        incidents: incidents.data?.items,
        services: services.data?.items,
        slos: sloItems,
        t,
        label: (g, v) => label(g, v),
      })
    : [];
  const unknownCount = tiles.filter((x) => x.status === 'unknown').length;
  const firstLoad = overview.loading && !o;
  const lastUpdated = overview.at;

  const update = (on: boolean) => {
    setAuto(on);
    writeAutoRefresh(on);
  };

  return (
    <>
      <PageHeader
        title={t('overview.title')}
        subtitle={t('overview.subtitle')}
        actions={<RefreshControls auto={auto} onAuto={update} at={lastUpdated} busy={busy} stale={Boolean(overview.error && o)} onRefresh={() => setTick((n) => n + 1)} />}
      >
        {o ? <HealthChips items={items} unknown={unknownCount} /> : null}
      </PageHeader>

      {overview.error && !o ? <DataError error={overview.error} onRetry={overview.reload} title={t('overview.loadFailed')} /> : null}

      <div className="flex flex-col gap-8">
        {o ? (
          items.length ? (
            <AttentionList items={items} />
          ) : (
            <Callout tone="ok" title={t('overview.attention.emptyTitle')}>
              {t('overview.attention.emptyBody')}
            </Callout>
          )
        ) : firstLoad ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : null}

        {o || firstLoad ? (
          <>
            <Group id="registry" icon={Globe} tiles={tiles} loading={firstLoad} gridClassName="sm:grid-cols-2 xl:grid-cols-3" />

            <div className="grid grid-cols-1 gap-x-4 gap-y-8 lg:grid-cols-2 xl:grid-cols-4">
              <Group id="money" icon={Wallet} tiles={tiles} loading={firstLoad} />
              <Group id="compliance" icon={ShieldCheck} tiles={tiles} loading={firstLoad} />
              <Group id="response" icon={Siren} tiles={tiles} loading={firstLoad} className="lg:col-span-2" gridClassName="sm:grid-cols-2" />
            </div>

            <Group id="platform" icon={Server} tiles={tiles} loading={firstLoad} gridClassName="sm:grid-cols-2 xl:grid-cols-4" />
          </>
        ) : null}

        {o ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title={t('overview.queueDepth.title')} description={t('overview.queueDepth.description')} actions={<LinkButton to="/ops/queues" variant="ghost">{t('overview.tile.queues.link')} <ArrowRight /></LinkButton>}>
              <QueueDepth queues={o.queues} />
            </Section>
            <Section title={t('overview.inFlight.title')} description={t('overview.inFlight.description')} actions={<LinkButton to="/ops/workflows" variant="ghost">{t('overview.tile.workflows.link')} <ArrowRight /></LinkButton>}>
              <InFlight o={o} />
            </Section>
            <Section title={t('overview.budgets.title')} description={t('overview.budgets.description')} actions={<LinkButton to="/ops/slo" variant="ghost">{t('overview.tile.slo.link')} <ArrowRight /></LinkButton>}>
              {slos.data ? <Budgets slos={slos.data.items} /> : <SectionError live={slos} />}
            </Section>
            <Section title={t('overview.registries.title')} description={t('overview.registries.description')} flush actions={<LinkButton to="/ops/registries" variant="ghost">{t('overview.tile.unknown.link')} <ArrowRight /></LinkButton>}>
              {registries.data ? (
                <Registries items={registries.data.items} />
              ) : (
                <div className="px-5 pb-5">
                  <SectionError live={registries} />
                </div>
              )}
            </Section>
          </div>
        ) : null}
      </div>

      <Footnote>{t('overview.footnote')}</Footnote>
    </>
  );
}
